import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, Lock } from 'lucide-react';
import { useConfigStore } from '../../store/useConfigStore';
import { useProjectStore } from '../../store/useProjectStore';
import { useResolvedWorkflow } from '../../hooks/useResolvedWorkflow';

/**
 * Statuses, types and defaults.
 *
 * This used to offer an editable "Kanban Columns / Statuses" list, and it had
 * stopped meaning anything. Since the workflow engine landed, a board resolves
 * its columns from the workflow bound to that project and work item type —
 * `config.statuses` survives only as the fallback for when no workflow resolves
 * at all. Editing it moved no column on any board.
 *
 * Worse, it looked authoritative while covering seven of the forty-four states
 * the workflows actually define. Adding a column here could not add a state a
 * work item was allowed to enter, because legal moves come from the workflow's
 * transitions, not from a list of names.
 *
 * So it shows the real thing instead: the columns the default project's board
 * will render, read from the workflow that governs it, with the way to change
 * them one click away.
 */
export default function SettingsPanel() {
  const { config } = useConfigStore();
  const { projects, fetchProjects } = useProjectStore();

  useEffect(() => {
    if (!projects.length) void fetchProjects();
  }, [projects.length, fetchProjects]);

  // The first project is what an unscoped board falls back to, so it is the one
  // whose columns this should show.
  const [projectId, setProjectId] = useState<string>('');
  const active = projectId || projects[0]?._id || '';
  const { workflow, columns, loading, isFallback } = useResolvedWorkflow(active || undefined);

  return (
    <div className="space-y-6">
      {/* ── Board columns ────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Board columns
          </h3>
          <Link
            to="/admin/workflows"
            className="text-xs text-accent hover:underline flex items-center gap-1 shrink-0"
          >
            Edit in Workflows <ExternalLink size={11} />
          </Link>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Columns and legal moves come from the workflow bound to a project, not
          from a list here — so this is what the board will show, and Workflows is
          where it changes.
        </p>

        {projects.length > 1 && (
          <select
            value={active}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full mb-3 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {projects.map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
            <Loader2 size={14} className="animate-spin" /> Resolving workflow…
          </div>
        ) : (
          <>
            <p className="text-xs mb-2">
              {workflow ? (
                <span className="text-gray-500 dark:text-gray-400">
                  From <strong className="text-gray-700 dark:text-gray-200">{workflow.name}</strong>
                  {' '}v{workflow.version}
                </span>
              ) : (
                // Named rather than hidden: a board with no workflow silently
                // shows the legacy list, and that is worth knowing about.
                <span className="text-amber-600 dark:text-amber-400">
                  No workflow resolved for this project — the board falls back to
                  the pre-workflow status list. Seed or bind a workflow in Workflows.
                </span>
              )}
            </p>

            <div className="space-y-1">
              {columns.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-700 rounded"
                >
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                  <span className="text-sm text-gray-800 dark:text-gray-200 flex-1">{c.label}</span>
                  {!isFallback && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
                      {c.category?.replace('_', ' ')}
                    </span>
                  )}
                  {c.wipLimit > 0 && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 shrink-0"
                      title="Work-in-progress limit"
                    >
                      WIP {c.wipLimit}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ── Work item hierarchy ──────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Work Item Hierarchy
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
          <Lock size={11} /> Fixed. The API enforces which parent each type may take.
        </p>
        <div className="space-y-1">
          {config.workItemTypes.map((t, i) => (
            <div key={t.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
              <span className="text-sm text-gray-800 dark:text-gray-200">{t.label}</span>
              {i < config.workItemTypes.length - 1 && (
                <span className="text-xs text-gray-400 ml-auto">→</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          Epic → Feature → Story / Bug → Task
        </p>
      </section>
    </div>
  );
}
